import { ContentChildren, Directive, ElementRef, HostListener, OnDestroy, QueryList } from '@angular/core';

import { ColumnDefinition, RowData } from '@anjuna/core';

import { DomOutlet } from '../common/template/DomOutlet';
import { Template } from '../common/template/Template';
import { TemplateDirective } from '../common/template/template.directive';

export interface RowContext {
    $implicit: RowData;
    column?: ColumnDefinition;
    index: number;
}

interface OutletMap {[templateName: string]: DomOutlet; }

/* tslint:disable:directive-selector */
@Directive({
    selector: 'anj-table:not([anjIgnore])',
})
export class AnjTable implements OnDestroy {
    @ContentChildren(TemplateDirective) private templates: QueryList<TemplateDirective>;

    public element: HTMLAnjTableElement;
    private outletRows: OutletMap[] = [];

    private static rowContext(row: RowData, index: number, column?: ColumnDefinition): RowContext {
        return {
            $implicit: row,
            column,
            index
        };
    }

    private static updateContext(outlet: DomOutlet, context: RowContext): void {
        outlet.updateContext(context);
    }

    constructor(elRef: ElementRef) {
        this.element = elRef.nativeElement;
        this.element.renderer = this;
    }

    public ngOnDestroy(): void {
        this.outletRows.map((rowMap: OutletMap) => {
            for (const templateName in rowMap) {
                if (rowMap[templateName]) {
                    rowMap[templateName] = rowMap[templateName].dispose();
                }
            }
            return rowMap;
        });
    }

    /**
     * Fixes https://git.zias.io/ui-foundation/anjuna/issues/876
     * When the columns change, destory all portals without killing the parent node
     */
    @HostListener('anjColumnsDidUpdate')
    public onColumnsUpdated() {
        this.outletRows.map((rowMap: OutletMap) => {
            for (const templateName in rowMap) {
                if (rowMap[templateName]) {
                    rowMap[templateName] = rowMap[templateName].dispose(false);
                }
            }
            return rowMap;
        });
    }

    /* These next three methods are a mess of duplicated logic - they will probably stay that way until feature complete, then I'll clean em up */
    public cellTemplate(cell: HTMLElement, offset: string, col: ColumnDefinition, row: RowData, rowIndex: number, template: string) {
        const rowMap = this.outletRows[rowIndex] || {};
        const outlet = rowMap[col.name];

        if (!cell) {
            /**
             * Removing the line:
             * rowMap[col.name] = outlet ? outlet.dispose() : undefined;
             * which fixes https://git.zias.io/ui-foundation/anjuna/issues/850.
             * Like the detail situation, I do not understand why this works..
             */
            return;
        }

        if (outlet) {
            if (cell !== outlet.targetElement) {
                this.updateOutlet(outlet, template, cell, AnjTable.rowContext(row, rowIndex, col));
            } else {
                AnjTable.updateContext(outlet, AnjTable.rowContext(row, rowIndex, col));
            }
        } else {
            rowMap[col.name] = this.createOutlet(cell, template, AnjTable.rowContext(row, rowIndex, col));
            this.outletRows[rowIndex] = rowMap;
        }

        col.initCell(cell, offset);
    }

    public rowTemplate(templateName: string, targetEl: HTMLElement, row: RowData, rowIndex: number): void {
        // Dispose row template no longer used for that index
        this.outletRows[rowIndex] = this.filterRowTemplates(this.outletRows[rowIndex], templateName);
        const outlet = this.outletRows[rowIndex][templateName];

        if (!targetEl) {
            this.outletRows[rowIndex][templateName] = outlet ? outlet.dispose() : undefined;
            return;
        }

        if (outlet) {
            if (targetEl !== outlet.targetElement) {
                this.updateOutlet(outlet, templateName, targetEl, AnjTable.rowContext(row, rowIndex));
            } else {
                AnjTable.updateContext(outlet, AnjTable.rowContext(row, rowIndex));
            }
        } else {
            this.outletRows[rowIndex][templateName] = this.createOutlet(targetEl, templateName, AnjTable.rowContext(row, rowIndex));
            this.outletRows[rowIndex] = this.outletRows[rowIndex];
        }
    }

    public fromTemplate(templateName: string, targetEl: HTMLElement, row: RowData, rowIndex: number): void {
        const rowMap = this.outletRows[rowIndex] || {};
        const outlet = rowMap[templateName];

        if (!targetEl) {
            /**
             * Removing the line:
             * rowMap[templateName] = outlet ? outlet.dispose() : undefined;
             * is part of the fix for https://git.zias.io/ui-foundation/anjuna/issues/750
             * However, I don't grok at all how/why this works. I don't buy it.
             * So when we eventually get an issue related to master detail, start here.
             * Put that line back, and fix it right.
             */
            return;
        }

        if (outlet) {
            if (targetEl !== outlet.targetElement) {
                this.updateOutlet(outlet, templateName, targetEl, AnjTable.rowContext(row, rowIndex));
            } else {
                AnjTable.updateContext(outlet, AnjTable.rowContext(row, rowIndex));
            }
        } else {
            rowMap[templateName] = this.createOutlet(targetEl, templateName, AnjTable.rowContext(row, rowIndex));
            this.outletRows[rowIndex] = rowMap;
        }
    }

    private createOutlet(target: Element, templateName: string, context: RowContext): DomOutlet {
        const outlet = new DomOutlet(target);
        outlet.attach(this.createTemplate(templateName, context));
        return outlet;
    }

    private createTemplate(templateName: string, context: RowContext): Template {
        const templateRef = this.templates.find(t => t.name === templateName);
        return new Template(templateRef, context);
    }

    private updateOutlet(outlet: DomOutlet, templateName: string, targetEl: HTMLElement, context: RowContext): void {
        outlet.targetElement = targetEl;
        outlet.detach();
        outlet.attach(this.createTemplate(templateName, context));
    }

    /**
     * Disposes of row templates no longer used in a given row. This ONLY happens if they have specified more than one
     * Template and the end user sorts or filters or otherwise changes the order. If only one template is specified,
     * the output will always match the input -r
     */
    private filterRowTemplates(rowMap: OutletMap = {}, templateName: string): OutletMap {
        const updated: OutletMap = {};

        Object.keys(rowMap).forEach(k => {
            if (k === templateName) {
                updated[k] = rowMap[k];
            } else {
                rowMap[k].dispose(false);
            }
        });

        return updated;
    }
}
